import React from 'react';
import { Box, Button, IconButton, Paper, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { newFanPollOption } from './fanPollModel';

const reorder = (options, from, to) => {
    if (to < 0 || to >= options.length) return options;
    const next = [...options];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next.map((option, index) => ({ ...option, displayOrder: index }));
};

export default function FanPollOptionsEditor({ options, error, onChange }) {
    const safeOptions = options ?? [];

    const updateLabel = (index, label) => onChange(safeOptions.map((option, current) => (
        current === index ? { ...option, label } : option
    )));

    const remove = (index) => onChange(safeOptions
        .filter((_, current) => current !== index)
        .map((option, displayOrder) => ({ ...option, displayOrder })));

    const add = () => onChange([
        ...safeOptions,
        newFanPollOption(safeOptions.length),
    ]);

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Options</Typography>
                    <Typography variant="caption" color="text.secondary">Between 2 and 8 choices.</Typography>
                </Box>
                <Button size="small" startIcon={<AddIcon />} onClick={add} disabled={safeOptions.length >= 8}>
                    Add option
                </Button>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {safeOptions.map((option, index) => (
                    <Box key={option.id ?? option._key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ width: 24, textAlign: 'right' }}>{index + 1}.</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            label={`Option ${index + 1}`}
                            value={option.label ?? ''}
                            onChange={(event) => updateLabel(index, event.target.value)}
                            inputProps={{ maxLength: 200 }}
                        />
                        <IconButton size="small" aria-label={`Move option ${index + 1} up`}
                            disabled={index === 0} onClick={() => onChange(reorder(safeOptions, index, index - 1))}>
                            <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" aria-label={`Move option ${index + 1} down`}
                            disabled={index === safeOptions.length - 1}
                            onClick={() => onChange(reorder(safeOptions, index, index + 1))}>
                            <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" aria-label={`Remove option ${index + 1}`}
                            disabled={safeOptions.length <= 2} onClick={() => remove(index)}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}
            </Box>
            {error && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{error}</Typography>}
        </Paper>
    );
}
